import { useRef, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { FolderOpen, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { productsApi } from '../../lib/api';

interface Category {
    id: string;
    externalId: number;
    name: string;
    slug: string;
    imageUrl: string | null;
    count: number;
}

export function CategoryRail() {
    const [searchParams] = useSearchParams();
    const currentCategory = searchParams.get('category');
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const { data: categories = [], isLoading } = useQuery({
        queryKey: ['store-categories'],
        queryFn: productsApi.getCategories,
        staleTime: 1000 * 60 * 10, // 10 minutes
    });

    // Check scroll position to show/hide arrows
    const checkScrollPosition = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    useEffect(() => {
        checkScrollPosition();
        const container = scrollRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollPosition);
            window.addEventListener('resize', checkScrollPosition);
            return () => {
                container.removeEventListener('scroll', checkScrollPosition);
                window.removeEventListener('resize', checkScrollPosition);
            };
        }
    }, [categories]);

    // Scroll by a fixed amount
    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const scrollAmount = 320; // ~2 cards
            const newScrollLeft = direction === 'left'
                ? scrollRef.current.scrollLeft - scrollAmount
                : scrollRef.current.scrollLeft + scrollAmount;

            scrollRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth',
            });
        }
    };

    if (isLoading) {
        return (
            <section className="py-12 bg-gray-50/50">
                <div className="container-custom">
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
                    </div>
                </div>
            </section>
        );
    }

    if (!categories.length) {
        return null;
    }

    return (
        <section className="py-12 bg-gray-50/50">
            <div className="container-custom">
                <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl md:text-3xl font-display font-bold text-secondary-900">
                        Explorer par Catégorie
                    </h2>

                    {/* Navigation Arrows - Desktop */}
                    <div className="hidden md:flex items-center gap-2">
                        <button
                            onClick={() => scroll('left')}
                            disabled={!canScrollLeft}
                            className={`p-2 rounded-full border transition-all ${
                                canScrollLeft
                                    ? 'border-gray-300 hover:border-primary-500 hover:bg-primary-50 text-gray-600 hover:text-primary-600'
                                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                            }`}
                            aria-label="Défiler vers la gauche"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => scroll('right')}
                            disabled={!canScrollRight}
                            className={`p-2 rounded-full border transition-all ${
                                canScrollRight
                                    ? 'border-gray-300 hover:border-primary-500 hover:bg-primary-50 text-gray-600 hover:text-primary-600'
                                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                            }`}
                            aria-label="Défiler vers la droite"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Categories Container */}
                <div className="relative">
                    {/* Left Gradient Fade */}
                    {canScrollLeft && (
                        <div className="absolute left-0 top-0 bottom-4 w-12 bg-gradient-to-r from-gray-50/90 to-transparent z-10 pointer-events-none" />
                    )}

                    {/* Right Gradient Fade */}
                    {canScrollRight && (
                        <div className="absolute right-0 top-0 bottom-4 w-12 bg-gradient-to-l from-gray-50/90 to-transparent z-10 pointer-events-none" />
                    )}

                    <div
                        ref={scrollRef}
                        className="flex gap-4 overflow-x-auto pb-4 pt-1 snap-x scrollbar-hide no-scrollbar scroll-smooth"
                    >
                    {(categories as Category[]).map((cat) => {
                        const isActive = currentCategory === cat.name;
                        return (
                            <Link
                                key={cat.id}
                                to={isActive ? '/' : `/?category=${encodeURIComponent(cat.name)}`}
                                className="snap-start shrink-0 group"
                            >
                                <motion.div
                                    whileHover={{ y: -8, scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className={`flex flex-col items-center gap-4 p-5 min-w-[160px] rounded-3xl transition-all border-2 ${isActive
                                        ? 'bg-primary-500 border-primary-500 shadow-xl shadow-primary-500/30'
                                        : 'bg-white border-transparent hover:border-primary-200 shadow-lg hover:shadow-xl'
                                        }`}
                                >
                                    {/* Category Image */}
                                    <div className={`w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden flex items-center justify-center transition-transform ${
                                        isActive ? 'bg-white/20 ring-4 ring-white/30' : 'bg-gradient-to-br from-gray-50 to-gray-100 ring-4 ring-gray-100'
                                    }`}>
                                        {cat.imageUrl ? (
                                            <img
                                                src={cat.imageUrl}
                                                alt={cat.name}
                                                className="w-full h-full object-contain p-2 transition-transform group-hover:scale-110"
                                            />
                                        ) : (
                                            <FolderOpen className={`w-12 h-12 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                                        )}
                                    </div>

                                    {/* Category Name */}
                                    <span className={`font-semibold text-center text-sm md:text-base leading-tight max-w-[120px] ${
                                        isActive ? 'text-white' : 'text-secondary-800'
                                    }`}>
                                        {cat.name}
                                    </span>

                                    {/* Product Count */}
                                    <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                                        isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                        {cat.count} produits
                                    </span>
                                </motion.div>
                            </Link>
                        );
                    })}
                    </div>
                </div>
            </div>
        </section>
    );
}
